// Lightweight Web Audio SFX + ambient loop for CA$H STRIKE.
// No external files — everything is synthesized with oscillators and noise
// buffers so bundle size stays flat. All calls are safe if audio is muted or
// the AudioContext hasn't been resumed by user gesture yet.

let ctx = null;
let masterGain = null;
let muted = false;
let ambientNodes = null;

const ensureCtx = () => {
    if (ctx) return ctx;
    try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return null;
        ctx = new AC();
        masterGain = ctx.createGain();
        // Louder overall so effect sounds carry the game (no music competing).
        masterGain.gain.value = muted ? 0 : 1.0;
        masterGain.connect(ctx.destination);
        return ctx;
    } catch (e) {
        return null;
    }
};

// Must be called from a user gesture (click / touch) on iOS.
export const primeAudio = () => {
    const c = ensureCtx();
    if (c && c.state === "suspended") c.resume().catch(() => {});
};

export const isMuted = () => muted;

export const setMuted = (v) => {
    muted = !!v;
    if (masterGain) masterGain.gain.value = muted ? 0 : 1.0;
};

// One-shot cannon boom (short low thump + noise burst). Beefier than before —
// this is now the game's signature "shoot" sound.
export const sfxCannon = () => {
    const c = ensureCtx();
    if (!c) return;
    const t = c.currentTime;

    // Sub-thump
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(180, t);
    osc.frequency.exponentialRampToValueAtTime(40, t + 0.22);
    g.gain.setValueAtTime(1.4, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.26);
    osc.connect(g).connect(masterGain);
    osc.start(t);
    osc.stop(t + 0.28);

    // Warm mid punch — the "pop" of the shot
    const punch = c.createOscillator();
    const pg = c.createGain();
    punch.type = "triangle";
    punch.frequency.setValueAtTime(500, t);
    punch.frequency.exponentialRampToValueAtTime(180, t + 0.08);
    pg.gain.setValueAtTime(0.75, t);
    pg.gain.exponentialRampToValueAtTime(0.001, t + 0.12);
    punch.connect(pg).connect(masterGain);
    punch.start(t);
    punch.stop(t + 0.14);

    // Short low-passed noise so it feels wet, not shrill
    const buf = c.createBuffer(1, c.sampleRate * 0.12, c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 2);
    const src = c.createBufferSource();
    const lp = c.createBiquadFilter();
    const ng = c.createGain();
    lp.type = "lowpass";
    lp.frequency.value = 1200;   // aggressive lowpass — no shrill highs
    ng.gain.value = 0.55;
    src.buffer = buf;
    src.connect(lp).connect(ng).connect(masterGain);
    src.start(t);
};

// Water splash on hit — hi-mid noise sweep. WARMER, softer top so no ice-pick highs.
export const sfxSplash = () => {
    const c = ensureCtx();
    if (!c) return;
    const t = c.currentTime;
    const buf = c.createBuffer(1, c.sampleRate * 0.35, c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 1.5);
    const src = c.createBufferSource();
    const bp = c.createBiquadFilter();
    const g = c.createGain();
    bp.type = "bandpass";
    bp.frequency.setValueAtTime(1400, t);
    bp.frequency.exponentialRampToValueAtTime(300, t + 0.3);
    bp.Q.value = 2;
    g.gain.setValueAtTime(0.9, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
    src.buffer = buf;
    src.connect(bp).connect(g).connect(masterGain);
    src.start(t);

    // Underlying thud so the box-hit feels solid
    const thud = c.createOscillator();
    const tg = c.createGain();
    thud.type = "sine";
    thud.frequency.setValueAtTime(140, t);
    thud.frequency.exponentialRampToValueAtTime(70, t + 0.15);
    tg.gain.setValueAtTime(0.5, t);
    tg.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
    thud.connect(tg).connect(masterGain);
    thud.start(t);
    thud.stop(t + 0.2);
};

// Coin ding on correct answer — bigger, chime-like.
export const sfxCoin = () => {
    const c = ensureCtx();
    if (!c) return;
    const t = c.currentTime;
    // Warmer pitches (avoid the previous shrill 1320Hz double-tap)
    const notes = [
        [660, 0.00, 0.35],
        [880, 0.06, 0.30],
        [1100,0.12, 0.28],
    ];
    notes.forEach(([f, off, dur]) => {
        const osc = c.createOscillator();
        const g = c.createGain();
        osc.type = "sine";           // pure sine = warm, no fizz
        osc.frequency.value = f;
        g.gain.setValueAtTime(0.001, t + off);
        g.gain.exponentialRampToValueAtTime(0.55, t + off + 0.01);
        g.gain.exponentialRampToValueAtTime(0.001, t + off + dur);
        osc.connect(g).connect(masterGain);
        osc.start(t + off);
        osc.stop(t + off + dur + 0.05);
    });
};

// Wrong answer buzz.
export const sfxWrong = () => {
    const c = ensureCtx();
    if (!c) return;
    const t = c.currentTime;
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(180, t);
    osc.frequency.exponentialRampToValueAtTime(70, t + 0.35);
    g.gain.setValueAtTime(0.5, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
    osc.connect(g).connect(masterGain);
    osc.start(t);
    osc.stop(t + 0.42);
};

// Money display — pleasing cash-register-ish jingle when coins get added to
// the score. Warmer, no shrill peaks.
export const sfxCashRegister = () => {
    const c = ensureCtx();
    if (!c) return;
    const t = c.currentTime;
    [523.25, 659.25, 783.99].forEach((f, i) => {
        const osc = c.createOscillator();
        const g = c.createGain();
        osc.type = "sine";
        osc.frequency.value = f;
        const off = i * 0.05;
        g.gain.setValueAtTime(0.001, t + off);
        g.gain.exponentialRampToValueAtTime(0.45, t + off + 0.01);
        g.gain.exponentialRampToValueAtTime(0.001, t + off + 0.35);
        osc.connect(g).connect(masterGain);
        osc.start(t + off);
        osc.stop(t + off + 0.4);
    });
};

// Game over — descending fanfare.
export const sfxGameOver = () => {
    const c = ensureCtx();
    if (!c) return;
    const t = c.currentTime;
    [660, 520, 390, 260].forEach((f, i) => {
        const osc = c.createOscillator();
        const g = c.createGain();
        osc.type = "square";
        osc.frequency.value = f;
        g.gain.setValueAtTime(0.001, t + i * 0.14);
        g.gain.exponentialRampToValueAtTime(0.25, t + i * 0.14 + 0.02);
        g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.14 + 0.18);
        osc.connect(g).connect(masterGain);
        osc.start(t + i * 0.14);
        osc.stop(t + i * 0.14 + 0.2);
    });
};

// Powerup grab — ascending arpeggio.
export const sfxPowerup = () => {
    const c = ensureCtx();
    if (!c) return;
    const t = c.currentTime;
    [523, 659, 784, 1047, 1319].forEach((f, i) => {
        const osc = c.createOscillator();
        const g = c.createGain();
        osc.type = "triangle";
        osc.frequency.value = f;
        g.gain.setValueAtTime(0.001, t + i * 0.06);
        g.gain.exponentialRampToValueAtTime(0.4, t + i * 0.06 + 0.01);
        g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.06 + 0.2);
        osc.connect(g).connect(masterGain);
        osc.start(t + i * 0.06);
        osc.stop(t + i * 0.06 + 0.22);
    });
};

// Streak celebration — warm triple ding (kept low-mid, no shrill highs).
export const sfxStreak = () => {
    const c = ensureCtx();
    if (!c) return;
    const t = c.currentTime;
    [523, 784, 1047].forEach((f, i) => {
        const osc = c.createOscillator();
        const g = c.createGain();
        osc.type = "sine";
        osc.frequency.value = f;
        g.gain.setValueAtTime(0.001, t + i * 0.08);
        g.gain.exponentialRampToValueAtTime(0.55, t + i * 0.08 + 0.01);
        g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.08 + 0.3);
        osc.connect(g).connect(masterGain);
        osc.start(t + i * 0.08);
        osc.stop(t + i * 0.08 + 0.32);
    });
};

// Ambient / music — DISABLED per user request. No pads, no wave-noise bed, no
// melody. Only one-shot SFX now, so effect sounds carry the whole game.
export const startAmbient = () => { /* no-op */ };
export const stopAmbient  = () => { ambientNodes = null; };

// ---- extra one-shot SFX for polish ----

// Parachute deploy — brief airy whoosh.
export const sfxWhoosh = () => {
    const c = ensureCtx();
    if (!c) return;
    const t = c.currentTime;
    const buf = c.createBuffer(1, c.sampleRate * 0.4, c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 2);
    const src = c.createBufferSource();
    const hp = c.createBiquadFilter();
    const g = c.createGain();
    hp.type = "highpass";
    hp.frequency.setValueAtTime(500, t);
    hp.frequency.exponentialRampToValueAtTime(2000, t + 0.4);
    g.gain.setValueAtTime(0.25, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
    src.buffer = buf;
    src.connect(hp).connect(g).connect(masterGain);
    src.start(t);
};

// Level up — bright chord stab.
export const sfxLevelUp = () => {
    const c = ensureCtx();
    if (!c) return;
    const t = c.currentTime;
    [523.25, 659.25, 783.99, 1046.50].forEach((f) => {
        const o = c.createOscillator();
        const g = c.createGain();
        o.type = "triangle";
        o.frequency.value = f;
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.32, t + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.55);
        o.connect(g).connect(masterGain);
        o.start(t);
        o.stop(t + 0.6);
    });
};
